import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { FLOW_VAR, RAG_VAR } from '../../../components/chart/rag';
import { errorMessage } from '../../../lib/error-message';
import { type ReportRange, reportApi, reportKeys } from '../api';

/**
 * Ước lượng so với thực tế — spec 16.4, dữ liệu nền của spec mục 10.
 *
 * Đường chéo là "đúng ước lượng": chấm nằm **trên** đường là lâu hơn dự tính,
 * **dưới** là nhanh hơn. Đó là toàn bộ cách đọc, và nó chỉ hoạt động khi hai
 * trục cùng đơn vị và cùng thang.
 *
 * ⚠️ Chấm **rỗng** là task từng bị chặn. Spec mục 10 nói rõ: chậm hơn ước lượng
 * không nhất thiết do năng lực, và một biểu đồ không phân biệt được hai thứ đó
 * sẽ được dùng để đánh giá người. Đây là lý do cột `wasBlocked` tồn tại.
 *
 * "Thực tế" là giờ task nằm ở cột "đang làm", quy theo ngày làm việc — hệ thống
 * chưa có bảng chấm công, và ghi chú dưới biểu đồ nói ra điều đó.
 */
export function EstimateAccuracyChart({
  projectId,
  range,
}: {
  projectId: string;
  range: ReportRange;
}): ReactNode {
  const { t } = useTranslation();

  const report = useQuery({
    queryKey: reportKeys.estimateAccuracy(projectId, range),
    queryFn: () => reportApi.estimateAccuracy(projectId, range),
  });

  const items = report.data?.items ?? [];
  const clean = items.filter((item) => !item.wasBlocked);
  const blocked = items.filter((item) => item.wasBlocked);
  const bound = Math.max(1, ...items.map((item) => Math.max(item.estimateHours, item.actualHours)));

  return (
    <ChartFrame
      title={t('reports.estimateTitle')}
      help="estimate"
      subtitle={
        report.data?.medianRatio == null
          ? t('reports.estimateNoData')
          : t('reports.estimateSubtitle', { ratio: report.data.medianRatio })
      }
      asOf={range.asOf ? new Date(range.asOf) : undefined}
      loading={report.isPending}
      error={report.isError ? errorMessage(report.error, t) : null}
      onRetry={() => void report.refetch()}
      empty={
        report.data && items.length === 0
          ? { title: t('reports.estimateEmpty'), description: t('reports.estimateEmptyHelp') }
          : null
      }
      legend={[
        { label: t('reports.estimatePerfect'), color: RAG_VAR.unknown },
        { label: t('reports.actualHours'), color: FLOW_VAR.inProgress },
        { label: t('reports.estimateBlocked'), color: RAG_VAR.amber },
      ]}
    >
      <div className="flex flex-col gap-1">
        <div className="h-44 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                type="number"
                dataKey="estimateHours"
                name={t('reports.estimateHours')}
                domain={[0, bound]}
                tick={{ fontSize: 11 }}
                stroke="var(--color-border)"
              />
              <YAxis
                type="number"
                dataKey="actualHours"
                name={t('reports.actualHours')}
                domain={[0, bound]}
                tick={{ fontSize: 11 }}
                stroke="var(--color-border)"
              />
              <ZAxis range={[40, 40]} />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                contentStyle={{
                  background: 'var(--color-background)',
                  border: '1px solid var(--color-border)',
                  fontSize: 12,
                }}
              />
              {/* Đường chéo est = actual. Không có nó thì scatter này chỉ là một đám chấm. */}
              <ReferenceLine
                segment={[
                  { x: 0, y: 0 },
                  { x: bound, y: bound },
                ]}
                stroke={RAG_VAR.unknown}
                strokeDasharray="4 4"
              />
              <Scatter data={clean} name={t('reports.actualHours')} fill={FLOW_VAR.inProgress} />
              <Scatter
                data={blocked}
                name={t('reports.estimateBlocked')}
                fill="transparent"
                stroke={RAG_VAR.amber}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        <p className="text-xs text-muted-foreground">{t('reports.estimateBlockedNote')}</p>

        <div className="grid gap-2 sm:grid-cols-2">
          <GroupList
            title={t('reports.estimateByAssignee')}
            groups={report.data?.byAssignee ?? []}
          />
          <GroupList title={t('reports.estimateByType')} groups={report.data?.byTaskType ?? []} />
        </div>
      </div>
    </ChartFrame>
  );
}

/**
 * Trung vị theo từng chiều.
 *
 * Trung vị chứ không phải trung bình: một task ước lượng 1 giờ mà mất 40 giờ
 * cho ra tỉ số 40, và nó kéo trung bình của cả một người lên mức vô nghĩa.
 */
function GroupList({
  title,
  groups,
}: {
  title: string;
  groups: readonly { label: string; taskCount: number; medianRatio: number }[];
}): ReactNode {
  const { t } = useTranslation();
  if (groups.length === 0) return null;

  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-xs font-medium">{title}</p>
      {groups.slice(0, 5).map((group) => (
        <div key={group.label} className="flex items-baseline gap-2 text-xs">
          <span className="truncate">
            {group.label === 'unassigned' ? t('reports.unassigned') : group.label}
          </span>
          <span
            className={`ml-auto shrink-0 ${group.medianRatio > 1.2 ? 'text-rag-amber' : 'text-muted-foreground'}`}
          >
            {t('reports.estimateMedian', {
              ratio: group.medianRatio,
              count: group.taskCount,
            })}
          </span>
        </div>
      ))}
    </div>
  );
}
